import { useMemo } from "react";
import { StyleSheet, useWindowDimensions } from "react-native";
import { colors } from "@/app/constants/styles";

const LANDSCAPE_BASE = { width: 1024, height: 768 };
const PORTRAIT_BASE = { width: 600, height: 1024 };
const MIN_SCALE = 0.6;
const MAX_SCALE = 1.5;

export interface ResponsiveLayout {
  scale: number;
  isPortrait: boolean;
}

export const getResponsiveLayout = (
  width: number,
  height: number,
): ResponsiveLayout => {
  const isPortrait = height > width;
  const base = isPortrait ? PORTRAIT_BASE : LANDSCAPE_BASE;
  const scale = Math.min(
    MAX_SCALE,
    Math.max(MIN_SCALE, Math.min(width / base.width, height / base.height)),
  );
  return { scale, isPortrait };
};

export const useResponsiveLayout = (): ResponsiveLayout => {
  const { width, height } = useWindowDimensions();
  return useMemo(() => getResponsiveLayout(width, height), [width, height]);
};

type Scale = (size: number) => number;

const useScaledStyles = <T>(create: (s: Scale) => T): T => {
  const { scale } = useResponsiveLayout();
  return useMemo(
    () => create((size) => Math.round(size * scale)),
    [create, scale],
  );
};

const createNumericKeypadStyles = (s: Scale) => {
  const keyWidth = s(156);
  const keyHeight = s(66);
  const keyGap = s(16);
  return StyleSheet.create({
    container: {
      gap: keyGap,
    },
    row: {
      flexDirection: "row",
      gap: keyGap,
    },
    key: {
      width: keyWidth,
      height: keyHeight,
      borderWidth: 1,
      borderColor: colors.albaikBrightRed,
      borderRadius: s(8),
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.white,
      shadowColor: colors.black,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.15,
      shadowRadius: 4,
      elevation: 2,
    },
    emptyKey: {
      width: keyWidth,
      height: keyHeight,
    },
    keyText: {
      fontSize: s(34),
      color: colors.albaikBrightRed,
    },
  });
};

export const useNumericKeypadStyles = () =>
  useScaledStyles(createNumericKeypadStyles);

const createOrderFieldStyles = (s: Scale) =>
  StyleSheet.create({
    container: {
      gap: s(12),
    },
    label: {
      fontSize: s(17),
      fontWeight: "500",
      letterSpacing: 1,
      color: colors.maroon,
    },
    required: {
      color: colors.albaikBrightRed,
    },
    inputRow: {
      flexDirection: "row",
      alignItems: "center",
    },
    prefix: {
      width: s(60),
      height: s(60),
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 2,
      borderRightWidth: 0,
      borderColor: colors.gray,
      backgroundColor: colors.dullWhite,
      borderTopLeftRadius: s(8),
      borderBottomLeftRadius: s(8),
    },
    prefixText: {
      fontSize: s(24),
      color: colors.black,
    },
    input: {
      flex: 1,
      height: s(60),
      paddingHorizontal: s(16),
      justifyContent: "center",
      borderWidth: 2,
      borderColor: colors.gray,
      borderRadius: s(8),
      backgroundColor: colors.white,
    },
    inputWithPrefix: {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
    activeInput: {
      borderColor: colors.albaikBrightRed,
    },
    valueText: {
      fontSize: s(24),
      color: colors.maroon,
    },
    placeholderText: {
      fontSize: s(20),
      color: colors.gray,
    },
  });

export const useOrderFieldStyles = () =>
  useScaledStyles(createOrderFieldStyles);

const createDeleteButtonStyles = (s: Scale) =>
  StyleSheet.create({
    button: {
      width: s(116),
      height: s(66),
      borderWidth: 1,
      borderColor: colors.albaikBrightRed,
      borderRadius: s(8),
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.white,
      shadowColor: colors.black,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.15,
      shadowRadius: 4,
      elevation: 2,
    },
    icon: {
      fontSize: s(34),
      color: colors.albaikBrightRed,
    },
  });

export const useDeleteButtonStyles = () =>
  useScaledStyles(createDeleteButtonStyles);

const createHeaderStyles = (s: Scale) =>
  StyleSheet.create({
    container: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: s(16),
      paddingVertical: s(8),
      gap: s(16),
      backgroundColor: colors.cardBackgroundWhite,
    },
    backButton: {
      flexDirection: "row",
      alignItems: "center",
      gap: s(8),
      minHeight: s(52),
      paddingHorizontal: s(20),
      borderWidth: 2,
      borderColor: colors.albaikBrightRed,
      borderRadius: s(8),
      backgroundColor: colors.white,
    },
    backButtonPressed: {
      backgroundColor: colors.dullWhite,
    },
    backButtonIcon: {
      fontSize: s(24),
      fontWeight: "bold",
      color: colors.albaikBrightRed,
    },
    backButtonText: {
      fontSize: s(20),
      fontWeight: "bold",
      color: colors.albaikBrightRed,
    },
    title: {
      fontSize: s(20),
      fontWeight: "500",
      color: colors.maroon,
    },
  });

export const useHeaderStyles = () => useScaledStyles(createHeaderStyles);

const createFooterButtonStyles = (s: Scale) =>
  StyleSheet.create({
    container: {
      alignItems: "center",
      paddingVertical: s(16),
      backgroundColor: colors.beige,
    },
    button: {
      minWidth: s(236),
      height: s(60),
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: s(40),
      borderRadius: s(4),
      backgroundColor: colors.albaikBrightRed,
    },
    disabledButton: {
      opacity: 0.5,
    },
    buttonText: {
      fontSize: s(22),
      fontWeight: "bold",
      color: colors.white,
    },
  });

export const useFooterButtonStyles = () =>
  useScaledStyles(createFooterButtonStyles);

const createPickupDriveThruStyles = (s: Scale) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.white,
    },
    scroll: {
      flex: 1,
    },
    content: {
      flexGrow: 1,
      alignItems: "center",
      paddingHorizontal: s(24),
      paddingVertical: s(48),
      gap: s(40),
    },
    heading: {
      fontSize: s(32),
      fontWeight: "bold",
      color: colors.maroon,
      textAlign: "center",
    },
    errorContainer: {
      backgroundColor: colors.errorBackground,
      borderRadius: 8,
      paddingHorizontal: s(16),
      paddingVertical: s(12),
      maxWidth: s(500),
    },
    errorContainerNoRetry: {
      backgroundColor: colors.warningBackground,
    },
    errorText: {
      fontSize: s(16),
      color: colors.errorText,
      textAlign: "center",
    },
    entryArea: {
      flexDirection: "row",
      alignItems: "stretch",
      gap: s(76),
    },
    entryAreaPortrait: {
      flexDirection: "column-reverse",
      alignItems: "center",
      gap: s(32),
    },
    fields: {
      width: s(358),
      justifyContent: "space-between",
    },
    fieldsPortrait: {
      gap: s(24),
    },
    fieldGroup: {
      gap: s(24),
    },
  });

export const usePickupDriveThruStyles = () => {
  const { isPortrait } = useResponsiveLayout();
  const styles = useScaledStyles(createPickupDriveThruStyles);
  return { styles, isPortrait };
};

const createPickupDriveThruVerifiedStyles = (s: Scale) => {
  const circleSize = s(150);
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.white,
    },
    scroll: {
      flex: 1,
    },
    content: {
      flexGrow: 1,
      alignItems: "center",
      paddingHorizontal: s(24),
      paddingVertical: s(32),
      gap: s(24),
    },
    title: {
      fontSize: s(48),
      fontWeight: "bold",
      color: colors.maroon,
      textAlign: "center",
    },
    message: {
      maxWidth: s(420),
      fontSize: s(16),
      lineHeight: s(24),
      textAlign: "center",
      color: colors.maroon,
    },
    checkCircle: {
      width: circleSize,
      height: circleSize,
      borderRadius: circleSize / 2,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.emerald,
    },
    checkMark: {
      fontSize: s(96),
      color: colors.white,
    },
  });
};

export const usePickupDriveThruVerifiedStyles = () =>
  useScaledStyles(createPickupDriveThruVerifiedStyles);
